import { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { RoleGuard } from '../components/RoleGuard';
import { useI18n, type TranslationKey } from '../i18n';
import {
  organizationsApi,
  type OrgMember,
  type OrganizationDetail,
} from '../api/organizations';

type OrgRole = 'org_admin' | 'maintenance' | 'user' | 'installer_admin' | 'installer_technician';

type TFn = ReturnType<typeof useI18n>['t'];

function roleLabel(t: TFn, role: OrgRole): string {
  const labels: Record<OrgRole, string> = {
    org_admin: t('team_role_org_admin'),
    maintenance: t('team_role_maintenance'),
    user: t('team_role_user'),
    installer_admin: t('team_role_installer_admin'),
    installer_technician: t('team_role_installer_technician'),
  };
  return labels[role] ?? role;
}

// Couleur de chaque rôle : barre de répartition et avatars
const ROLE_COLOR: Record<OrgRole, string> = {
  org_admin: '#a78bfa',
  maintenance: '#fbbf24',
  user: '#94a3b8',
  installer_admin: '#2dd4bf',
  installer_technician: '#22d3ee',
};
const AVATAR_BG = ['bg-blue-100 text-blue-700', 'bg-teal-100 text-teal-700', 'bg-violet-100 text-violet-700', 'bg-amber-100 text-amber-700', 'bg-pink-100 text-pink-700', 'bg-emerald-100 text-emerald-700'];

const ROLE_BADGE: Record<OrgRole, string> = {
  org_admin: 'bg-purple-100 text-purple-700',
  maintenance: 'bg-amber-100 text-amber-700',
  user: 'bg-gray-100 text-gray-700',
  installer_admin: 'bg-teal-100 text-teal-700',
  installer_technician: 'bg-cyan-100 text-cyan-700',
};

// Rôle plateforme → libellé traduit
const PLATFORM_ROLE_KEYS: Record<string, TranslationKey> = {
  super_admin: 'pg_role_super_admin',
  org_admin: 'team_role_org_admin',
  maintenance: 'team_role_maintenance',
  user: 'team_role_user',
};

// Rôles d'organisation hérités (absents de ROLE_BADGE) → libellé traduit
const OTHER_ROLE_KEYS: Record<string, TranslationKey> = {
  admin: 'pg_role_admin',
};

export default function MembersPage() {
  const { user, currentMembership, hasOrgRole } = useAuth();
  const { t, locale } = useI18n();
  const [org, setOrg] = useState<OrganizationDetail | null>(null);
  const [members, setMembers] = useState<OrgMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showInvite, setShowInvite] = useState(false);
  const [pendingRoleChange, setPendingRoleChange] = useState<{ member: OrgMember; role: OrgRole } | null>(null);
  const [pendingRemove, setPendingRemove] = useState<OrgMember | null>(null);
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState<'all' | 'admins' | OrgRole>('all');

  const isAdmin = hasOrgRole('org_admin', 'installer_admin');

  const isAdminRole = (r: OrgRole) => r === 'org_admin' || r === 'installer_admin';
  // Filtered members based on role tab and search
  const filtered = members.filter(m => {
    if (roleFilter === 'admins' && !isAdminRole(m.orgRole)) return false;
    if (roleFilter !== 'all' && roleFilter !== 'admins' && m.orgRole !== roleFilter) return false;
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    const full = [m.firstName, m.lastName, m.userEmail, m.orgRole].join(' ').toLowerCase();
    return full.includes(q);
  });

  const refresh = async () => {
    setLoading(true);
    setError(null);
    try {
      const [o, m] = await Promise.all([
        organizationsApi.getOrganization('current'),
        organizationsApi.listMembers('current'),
      ]);
      setOrg(o);
      setMembers(m);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
  }, []);

  const handleRoleChange = (m: OrgMember, role: OrgRole) => {
    if (m.orgRole === role) return;
    // Open password confirmation modal — the actual API call happens after password validation.
    setPendingRoleChange({ member: m, role });
  };

  const confirmRoleChange = async (password: string) => {
    if (!pendingRoleChange) return;
    const { member, role } = pendingRoleChange;
    await organizationsApi.updateRole('current', member.userId, role as any, password);
    setPendingRoleChange(null);
    const promotedSomeoneElse = role === 'org_admin' && member.userId !== user?.userId;
    const changedSelf = member.userId === user?.userId;
    if (promotedSomeoneElse || changedSelf) {
      window.location.reload();
      return;
    }
    await refresh();
  };

  const handleRemove = (m: OrgMember) => {
    setPendingRemove(m);
  };

  const confirmRemove = async (password: string) => {
    if (!pendingRemove) return;
    const m = pendingRemove;
    await organizationsApi.removeMember('current', m.userId, password);
    setPendingRemove(null);
    if (m.userId === user?.userId) {
      window.location.href = '/login';
      return;
    }
    await refresh();
  };

  // Chiffres du bandeau (calculés sur la liste des membres)
  const nameOf = (m: OrgMember) => [m.firstName, m.lastName].filter(Boolean).join(' ') || m.userEmail;
  const initialsOf = (m: OrgMember) => {
    const full = [m.firstName, m.lastName].filter(Boolean).join(' ');
    return (full.match(/\b\w/g) || []).slice(0, 2).join('').toUpperCase() || m.userEmail[0].toUpperCase();
  };
  const roleCounts = members.reduce<Record<string, number>>((acc, m) => { acc[m.orgRole] = (acc[m.orgRole] || 0) + 1; return acc; }, {});
  const roleOrder = (Object.keys(ROLE_COLOR) as OrgRole[]).filter(r => roleCounts[r]);
  const admins = members.filter(m => isAdminRole(m.orgRole)).length;
  const newest = [...members].sort((a, b) => new Date(b.joinedAt).getTime() - new Date(a.joinedAt).getTime())[0];
  const tabs: Array<{ key: 'all' | 'admins' | OrgRole; label: string; n: number }> = [
    { key: 'all', label: t('team_mb_tab_all'), n: members.length },
    { key: 'admins', label: t('team_mb_tab_admins'), n: admins },
    ...roleOrder.filter(r => !isAdminRole(r)).map(r => ({ key: r, label: roleLabel(t, r), n: roleCounts[r] })),
  ];
  const widget = 'rounded-2xl border border-white/15 bg-white/[0.06] px-4 py-3.5 min-w-0';

  return (
    <div className="space-y-6">
      {/* ── Bandeau à widgets ── */}
      <section className="relative overflow-hidden rounded-3xl bg-[#0b2a4d] text-white p-6 shadow-xl">
        <div aria-hidden className="absolute inset-0" style={{ background: 'radial-gradient(60% 90% at 90% 0%, rgba(52,211,153,.32), transparent 60%), linear-gradient(120deg, #0d3b66, #082040)' }} />
        <div aria-hidden className="absolute inset-0" style={{ backgroundImage: 'linear-gradient(rgba(255,255,255,.05) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,.05) 1px, transparent 1px)', backgroundSize: '32px 32px' }} />
        <div className="relative flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            {org && <p className="text-xs text-white/70">{org.name} <span className="text-white/45">· {org.slug}</span></p>}
            <h1 className="text-2xl sm:text-3xl font-extrabold text-white font-heading tracking-tight mt-1">{t('team_members_title')}</h1>
          </div>
          <RoleGuard orgRoles={['org_admin', 'installer_admin']} platformRoles={['super_admin']}>
            <button onClick={() => setShowInvite(true)} className="inline-flex items-center gap-2 h-10 px-4 rounded-xl bg-white text-zce-navy text-sm font-bold shadow-md hover:bg-blue-50 self-start sm:self-auto">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2} strokeLinecap="round" aria-hidden><path d="M12 5v14M5 12h14" /></svg>
              {t('team_members_invite')}
            </button>
          </RoleGuard>
        </div>

        <div className="relative grid gap-3 mt-5 grid-cols-1 sm:grid-cols-2 lg:grid-cols-4">
          <div className={widget}>
            <div className="text-xs text-white/75">{t('team_mb_w_members')}</div>
            <div className="flex items-center justify-between gap-3 mt-1.5">
              <span className="font-mono tabular-nums text-[30px] font-semibold">{loading ? '—' : members.length}</span>
              <span className="flex">
                {members.slice(0, 5).map((m, i) => (
                  <span key={m.membershipId} className="w-8 h-8 rounded-full border-2 border-[#0b2a4d] flex items-center justify-center text-[11px] font-bold text-[#0b2a4d]" style={{ marginLeft: i ? -8 : 0, background: ROLE_COLOR[m.orgRole] ?? '#94a3b8' }} title={nameOf(m)}>{initialsOf(m)}</span>
                ))}
              </span>
            </div>
          </div>
          <div className={widget}>
            <div className="text-xs text-white/75">{t('team_mb_w_roles')}</div>
            <div className="flex h-2.5 rounded-full overflow-hidden mt-4 bg-white/10">
              {roleOrder.map(r => <span key={r} style={{ width: `${(roleCounts[r] / (members.length || 1)) * 100}%`, background: ROLE_COLOR[r] }} />)}
            </div>
            <div className="flex flex-wrap gap-x-3 gap-y-1 mt-2.5 text-[11px] text-white/80">
              {roleOrder.map(r => <span key={r} className="inline-flex items-center gap-1.5"><span className="w-2 h-2 rounded-full" style={{ background: ROLE_COLOR[r] }} />{roleCounts[r]} {roleLabel(t, r)}</span>)}
            </div>
          </div>
          <div className={widget}>
            <div className="text-xs text-white/75">{t('team_mb_w_admins')}</div>
            <div className="font-mono tabular-nums text-[30px] font-semibold mt-1">{loading ? '—' : admins}</div>
            <div className="text-[11px] text-white/60 mt-0.5">{t('team_mb_w_admins_foot')}</div>
          </div>
          <div className={widget}>
            <div className="text-xs text-white/75">{t('team_mb_w_newest')}</div>
            <div className="font-bold mt-2 truncate">{newest ? nameOf(newest) : '—'}</div>
            <div className="text-[11px] text-white/60 mt-0.5">{newest ? t('team_mb_w_newest_foot', { date: new Date(newest.joinedAt).toLocaleDateString(locale) }) : ''}</div>
          </div>
        </div>
      </section>

      {/* ── Error ── */}
      {error && (
        <div className="p-4 rounded-xl bg-red-50 border border-red-200 text-red-700 text-sm shadow-sm">
          {error}
        </div>
      )}

      {/* ── Loading ── */}
      {loading ? (
        <div className="text-center py-16 text-zce-gray-muted">
          <div className="inline-block w-8 h-8 border-3 border-zce-navy/20 border-t-zce-navy rounded-full animate-spin mb-3" />
          <p className="text-sm">{t('team_members_loading')}</p>
        </div>
      ) : (
        <div className="bg-white rounded-2xl shadow-zce border border-gray-200 overflow-hidden">
          {/* Filtres + recherche */}
          <div className="flex flex-wrap items-center justify-between gap-3 px-4 sm:px-5 py-3.5 border-b border-gray-100">
            <div role="tablist" aria-label={t('team_mb_filter')} className="flex flex-wrap gap-1 p-1 rounded-xl bg-gray-100">
              {tabs.map(tab => (
                <button key={tab.key} type="button" role="tab" aria-selected={roleFilter === tab.key} onClick={() => setRoleFilter(tab.key)}
                  className={`h-8 px-3 rounded-lg text-[13px] font-semibold transition-colors ${roleFilter === tab.key ? 'bg-white text-zce-navy shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}>
                  {tab.label} <span className="font-mono tabular-nums">· {tab.n}</span>
                </button>
              ))}
            </div>
            <div className="relative w-full sm:w-72">
              <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zce-gray-muted" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              <input
                type="text"
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder={t('team_members_search')}
                aria-label={t('team_members_search')}
                className="w-full h-10 pl-9 pr-3 text-sm rounded-xl border border-gray-200 bg-white focus:outline-none focus:ring-2 focus:ring-cyan-500/30 focus:border-cyan-400 placeholder:text-zce-gray-muted"
              />
            </div>
          </div>

          {/* Tableau des membres */}
          <div className="overflow-x-auto">
            <table className="w-full min-w-[760px] text-sm">
              <thead>
                <tr className="text-left text-[11px] uppercase tracking-wider text-gray-400">
                  <th className="px-5 py-3 font-semibold">{t('team_mb_th_member')}</th>
                  <th className="px-3 py-3 font-semibold">{t('team_members_orgRole')}</th>
                  <th className="px-3 py-3 font-semibold">{t('team_members_platform')}</th>
                  <th className="px-3 py-3 font-semibold">{t('team_members_joinedOn')}</th>
                  <th className="px-5 py-3 font-semibold text-right">{t('team_mb_th_actions')}</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((m, idx) => {
                  const isSelf = m.userId === user?.userId;
                  const isPlatformSuperAdmin = m.platformRole === 'super_admin';
                  return (
                    <tr key={m.membershipId} className={`border-t border-gray-100 hover:bg-gray-50 ${isSelf ? 'bg-cyan-50/40' : ''}`}>
                      <td className="px-5 py-3">
                        <div className="flex items-center gap-3 min-w-0">
                          <span className={`w-9 h-9 rounded-full flex items-center justify-center text-xs font-bold shrink-0 ${AVATAR_BG[idx % AVATAR_BG.length]}`}>{initialsOf(m)}</span>
                          <div className="min-w-0">
                            <div className="flex items-center gap-1.5">
                              <span className="font-semibold text-zce-navy truncate">{[m.firstName, m.lastName].filter(Boolean).join(' ') || '—'}</span>
                              {isSelf && <span className="shrink-0 text-[10px] font-medium bg-cyan-100 text-cyan-700 px-1.5 py-0.5 rounded-full">{t('team_members_you')}</span>}
                            </div>
                            <span className="text-xs text-zce-gray-muted truncate block">{m.userEmail}</span>
                          </div>
                        </div>
                      </td>
                      <td className="px-3 py-3">
                        {isAdmin && !isPlatformSuperAdmin ? (
                          <select
                            value={m.orgRole}
                            onChange={(e) => handleRoleChange(m, e.target.value as OrgRole)}
                            aria-label={t('team_members_orgRole')}
                            className="text-xs rounded-lg border border-gray-200 px-2 py-1.5 bg-white font-medium text-zce-navy"
                          >
                            <option value="org_admin">{t('team_role_org_admin')}</option>
                            <option value="maintenance">{t('team_role_maintenance')}</option>
                            <option value="user">{t('team_role_user')}</option>
                          </select>
                        ) : (
                          <span className={`px-2.5 py-1 text-[11px] rounded-full font-medium ${ROLE_BADGE[m.orgRole]}`}>{roleLabel(t, m.orgRole)}</span>
                        )}
                      </td>
                      <td className="px-3 py-3 text-gray-600 capitalize">{PLATFORM_ROLE_KEYS[m.platformRole] ? t(PLATFORM_ROLE_KEYS[m.platformRole]) : m.platformRole.replace('_', ' ')}</td>
                      <td className="px-3 py-3 text-gray-600 font-mono tabular-nums text-[13px]">{new Date(m.joinedAt).toLocaleDateString(locale)}</td>
                      <td className="px-5 py-3 text-right">
                        {isAdmin && !isPlatformSuperAdmin && (
                          <button
                            onClick={() => handleRemove(m)}
                            className="h-8 px-3 text-xs font-semibold text-rose-600 bg-white hover:bg-rose-50 rounded-lg border border-gray-200 hover:border-rose-200 transition-colors"
                          >
                            {isSelf ? t('team_members_leaveOrg') : t('team_members_removeMember')}
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {filtered.length === 0 && (
              <div className="text-center py-12 text-zce-gray-muted text-sm">
                {search ? t('team_noResults') : t('team_members_none')}
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── Footer info ── */}
      {currentMembership && (
        <div className="text-xs text-zce-gray-muted text-center py-2">
          {t('team_members_signedInAs')} <strong className="text-zce-navy">{currentMembership.role in ROLE_BADGE ? roleLabel(t, currentMembership.role as OrgRole) : OTHER_ROLE_KEYS[currentMembership.role] ? t(OTHER_ROLE_KEYS[currentMembership.role]) : currentMembership.role.replace('_', ' ')}</strong> {t('team_members_signedInOn')} <strong className="text-zce-navy">{currentMembership.orgName}</strong>
        </div>
      )}

      {/* ── Modals ── */}
      {showInvite && (
        <InviteModal
          isInstaller={currentMembership?.role === 'installer_admin' || currentMembership?.role === 'installer_technician'}
          onClose={() => setShowInvite(false)}
          onCreated={async () => {
            setShowInvite(false);
            await refresh();
          }}
        />
      )}

      {pendingRoleChange && (
        <ConfirmRoleChangeModal
          member={pendingRoleChange.member}
          newRole={pendingRoleChange.role}
          onCancel={() => setPendingRoleChange(null)}
          onConfirm={confirmRoleChange}
        />
      )}

      {pendingRemove && (
        <ConfirmRemoveMemberModal
          member={pendingRemove}
          isSelf={pendingRemove.userId === user?.userId}
          onCancel={() => setPendingRemove(null)}
          onConfirm={confirmRemove}
        />
      )}
    </div>
  );
}

// ─── Invite Modal ───
function InviteModal({ onClose, onCreated, isInstaller }: { onClose: () => void; onCreated: () => void; isInstaller?: boolean }) {
  const { t } = useI18n();
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<OrgRole>(isInstaller ? ('installer_technician' as OrgRole) : 'user');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<{ link: string; emailSent: boolean } | null>(null);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const inv = await organizationsApi.inviteMember('current', { email, role: role as any });
      setResult({ link: inv.link, emailSent: !!(inv as any).emailSent });
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setSubmitting(false);
    }
  };

  const copyLink = async () => {
    if (!result?.link) return;
    try {
      await navigator.clipboard.writeText(result.link);
      alert(t('team_invite_linkCopied'));
    } catch {
      // ignore
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-lg font-semibold">{t('team_members_invite')}</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">✕</button>
        </div>

        {result ? (
          <div className="space-y-3">
            <div className={`p-3 border text-sm rounded ${
              result.emailSent
                ? 'bg-green-50 border-green-200 text-green-800'
                : 'bg-amber-50 border-amber-200 text-amber-800'
            }`}>
              {result.emailSent
                ? <>✅ {t('team_invite_sentTo')} <strong>{email}</strong>.</>
                : <>⚠️ {t('team_invite_emailFailed')}</>}
            </div>
            <details className="text-xs text-gray-500">
              <summary className="cursor-pointer">{t('team_invite_showLink')}</summary>
              <textarea readOnly value={result.link} rows={3}
                className="w-full mt-2 px-3 py-2 border border-gray-300 rounded-md text-xs font-mono" />
              <button type="button" onClick={copyLink}
                className="mt-2 px-3 py-1 text-xs rounded border border-gray-300 hover:bg-gray-50">
                {t('team_invite_copyLink')}
              </button>
            </details>
            <div className="flex justify-end pt-2">
              <button onClick={() => onCreated()}
                className="px-4 py-2 text-sm rounded-md bg-primary-600 text-white hover:bg-primary-700">
                {t('team_invite_done')}
              </button>
            </div>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('team_invite_email')} *</label>
              <input
                type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                placeholder="user@example.com"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('team_invite_role')} *</label>
              <select value={role} onChange={(e) => setRole(e.target.value as OrgRole)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm bg-white">
                {isInstaller ? (
                  <>
                    <option value="installer_technician">{t('team_invite_opt_installer_technician')}</option>
                    <option value="installer_admin">{t('team_invite_opt_installer_admin')}</option>
                  </>
                ) : (
                  <>
                    <option value="user">{t('team_invite_opt_user')}</option>
                    <option value="maintenance">{t('team_invite_opt_maintenance')}</option>
                    <option value="org_admin">{t('team_invite_opt_org_admin')}</option>
                  </>
                )}
              </select>
            </div>
            <p className="text-xs text-gray-500">
              {t('team_invite_help')}
            </p>
            {error && <div className="p-2 bg-red-50 border border-red-200 text-red-700 text-sm rounded">{error}</div>}
            <div className="flex justify-end gap-2 pt-2">
              <button type="button" onClick={onClose}
                className="px-4 py-2 text-sm rounded-md border border-gray-300 hover:bg-gray-50">
                {t('team_cancel')}
              </button>
              <button type="submit" disabled={submitting}
                className="px-4 py-2 text-sm rounded-md bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-50">
                {submitting ? t('team_invite_sending') : t('team_invite_send')}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

// ─── Confirm Role Change Modal (re-auth with password) ───
function ConfirmRoleChangeModal({
  member,
  newRole,
  onCancel,
  onConfirm,
}: {
  member: OrgMember;
  newRole: OrgRole;
  onCancel: () => void;
  onConfirm: (password: string) => Promise<void>;
}) {
  const { t } = useI18n();
  const [password, setPassword] = useState('');
  const [showPwd, setShowPwd] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await onConfirm(password);
    } catch (err) {
      setError((err as Error).message);
      setSubmitting(false);
    }
  };

  const fullName = [member.firstName, member.lastName].filter(Boolean).join(' ') || member.userEmail;
  const initials = (fullName.match(/\b\w/g) || []).slice(0, 2).join('').toUpperCase();
  const willBecomeAdmin = newRole === 'org_admin';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 backdrop-blur-sm p-4">
      <div className="bg-white text-slate-800 rounded-xl shadow-2xl w-full max-w-lg overflow-hidden ring-1 ring-slate-200">
        {/* Header */}
        <div className="px-6 py-4 border-b border-slate-200 bg-gradient-to-br from-slate-50 to-white flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-primary-100 text-primary-700 flex items-center justify-center">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none"
                 stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
                 className="w-5 h-5">
              <path d="M12 1l3 6 6 .9-4.5 4.4 1 6.7L12 16l-5.5 3 1-6.7L3 7.9 9 7z" />
            </svg>
          </div>
          <div className="flex-1">
            <h2 className="text-base font-semibold text-slate-900">{t('team_roleChange_title')}</h2>
            <p className="text-xs text-slate-500 mt-0.5">{t('team_sensitiveAction')}</p>
          </div>
          <button onClick={onCancel} aria-label={t('team_close')}
                  className="text-slate-400 hover:text-slate-600 transition rounded p-1">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none"
                 stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
                 className="w-5 h-5"><path d="M18 6L6 18M6 6l12 12" /></svg>
          </button>
        </div>

        {/* Body */}
        <div className="px-6 py-5 space-y-5">
          {/* Member card */}
          <div className="flex items-center gap-3 p-3 rounded-lg bg-slate-50 border border-slate-200">
            <div className="w-10 h-10 rounded-full bg-primary-600 text-white flex items-center justify-center text-sm font-semibold shrink-0">
              {initials || '·'}
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-sm font-medium text-slate-900 truncate">{fullName}</div>
              <div className="text-xs text-slate-500 truncate">{member.userEmail}</div>
            </div>
          </div>

          {/* Role transition */}
          <div className="flex items-center justify-center gap-3 py-1">
            <span className={`px-3 py-1.5 text-xs rounded-full font-medium ${ROLE_BADGE[member.orgRole]}`}>
              {roleLabel(t, member.orgRole)}
            </span>
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none"
                 stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
                 className="w-5 h-5 text-slate-400">
              <path d="M5 12h14M13 6l6 6-6 6" />
            </svg>
            <span className={`px-3 py-1.5 text-xs rounded-full font-medium ${ROLE_BADGE[newRole]}`}>
              {roleLabel(t, newRole)}
            </span>
          </div>

          {/* Warning */}
          {willBecomeAdmin && (
            <div className="flex gap-2 p-3 rounded-lg bg-amber-50 border border-amber-200 text-amber-800 text-xs">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none"
                   stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
                   className="w-4 h-4 shrink-0 mt-0.5">
                <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z M12 9v4 M12 17h.01" />
              </svg>
              <span>
                {t('team_roleChange_warn1')} <strong>{roleLabel(t, 'org_admin')}</strong>{t('team_roleChange_warn2')}{' '}
                <strong>{roleLabel(t, 'user')}</strong> {t('team_roleChange_warn3')}
              </span>
            </div>
          )}

          {/* Password */}
          <form onSubmit={submit} className="space-y-3" id="confirm-role-form">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">
                {t('team_password')}
              </label>
              <div className="relative">
                <input
                  type={showPwd ? 'text' : 'password'} required autoFocus value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full px-3 py-2.5 pr-10 bg-white border border-slate-300 rounded-lg text-sm
                             text-slate-900 placeholder-slate-400
                             focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  placeholder={t('team_passwordPlaceholder')}
                />
                <button type="button" tabIndex={-1} onClick={() => setShowPwd(s => !s)}
                        className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-slate-400 hover:text-slate-600">
                  {showPwd ? (
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none"
                         stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
                         className="w-4 h-4">
                      <path d="M17.94 17.94A10.07 10.07 0 0112 20c-7 0-11-8-11-8a18.45 18.45 0 015.06-5.94M9.9 4.24A9.12 9.12 0 0112 4c7 0 11 8 11 8a18.5 18.5 0 01-2.16 3.19m-6.72-1.07a3 3 0 11-4.24-4.24M1 1l22 22" />
                    </svg>
                  ) : (
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none"
                         stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
                         className="w-4 h-4">
                      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                      <circle cx="12" cy="12" r="3" />
                    </svg>
                  )}
                </button>
              </div>
            </div>
            {error && (
              <div className="flex items-start gap-2 p-3 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none"
                     stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
                     className="w-4 h-4 shrink-0 mt-0.5">
                  <circle cx="12" cy="12" r="10" /><path d="M12 8v4 M12 16h.01" />
                </svg>
                <span>{error}</span>
              </div>
            )}
          </form>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 bg-slate-50 border-t border-slate-200 flex justify-end gap-2">
          <button type="button" onClick={onCancel} disabled={submitting}
                  className="px-4 py-2 text-sm font-medium rounded-lg border border-slate-300 text-slate-700 bg-white hover:bg-slate-100 transition disabled:opacity-50">
            {t('team_cancel')}
          </button>
          <button type="submit" form="confirm-role-form" disabled={submitting || !password}
                  className="px-4 py-2 text-sm font-medium rounded-lg bg-primary-600 text-white hover:bg-primary-700 transition shadow-sm disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-2">
            {submitting && (
              <svg className="w-4 h-4 animate-spin" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
              </svg>
            )}
            {submitting ? t('team_roleChange_verifying') : t('team_roleChange_confirm')}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Confirm Remove Member Modal (re-auth with password) ───
function ConfirmRemoveMemberModal({
  member,
  isSelf,
  onCancel,
  onConfirm,
}: {
  member: OrgMember;
  isSelf: boolean;
  onCancel: () => void;
  onConfirm: (password: string) => Promise<void>;
}) {
  const { t } = useI18n();
  const [password, setPassword] = useState('');
  const [showPwd, setShowPwd] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await onConfirm(password);
    } catch (err) {
      setError((err as Error).message);
      setSubmitting(false);
    }
  };

  const fullName = [member.firstName, member.lastName].filter(Boolean).join(' ') || member.userEmail;
  const initials = (fullName.match(/\b\w/g) || []).slice(0, 2).join('').toUpperCase();

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 backdrop-blur-sm p-4">
      <div className="bg-white text-slate-800 rounded-xl shadow-2xl w-full max-w-lg overflow-hidden ring-1 ring-slate-200">
        {/* Header */}
        <div className="px-6 py-4 border-b border-slate-200 bg-gradient-to-br from-red-50 to-white flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-red-100 text-red-700 flex items-center justify-center">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none"
                 stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
                 className="w-5 h-5">
              <path d="M16 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2 M23 11l-6 6 M17 11l6 6" />
              <circle cx="8.5" cy="7" r="4" />
            </svg>
          </div>
          <div className="flex-1">
            <h2 className="text-base font-semibold text-slate-900">
              {isSelf ? t('team_members_leaveOrg') : t('team_remove_title')}
            </h2>
            <p className="text-xs text-slate-500 mt-0.5">{t('team_sensitiveAction')}</p>
          </div>
          <button onClick={onCancel} aria-label={t('team_close')}
                  className="text-slate-400 hover:text-slate-600 transition rounded p-1">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none"
                 stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
                 className="w-5 h-5"><path d="M18 6L6 18M6 6l12 12" /></svg>
          </button>
        </div>

        {/* Body */}
        <div className="px-6 py-5 space-y-5">
          {/* Member card */}
          <div className="flex items-center gap-3 p-3 rounded-lg bg-slate-50 border border-slate-200">
            <div className="w-10 h-10 rounded-full bg-red-600 text-white flex items-center justify-center text-sm font-semibold shrink-0">
              {initials || '·'}
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-sm font-medium text-slate-900 truncate">{fullName}</div>
              <div className="text-xs text-slate-500 truncate">{member.userEmail}</div>
            </div>
            <span className={`px-2 py-1 text-xs rounded-full font-medium ${ROLE_BADGE[member.orgRole]}`}>
              {roleLabel(t, member.orgRole)}
            </span>
          </div>

          {/* Warning */}
          <div className="flex gap-2 p-3 rounded-lg bg-red-50 border border-red-200 text-red-800 text-sm">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none"
                 stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
                 className="w-5 h-5 shrink-0 mt-0.5">
              <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z M12 9v4 M12 17h.01" />
            </svg>
            <div>
              {isSelf ? (
                <>{t('team_remove_selfWarn1')} <strong>{t('team_remove_selfWarn2')}</strong>{t('team_remove_selfWarn3')}</>
              ) : (
                <>{t('team_remove_warn1')} <strong>{t('team_remove_warn2')}</strong> {t('team_remove_warn3')}</>
              )}
            </div>
          </div>

          {/* Password */}
          <form onSubmit={submit} className="space-y-3" id="confirm-remove-form">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">
                {t('team_password')}
              </label>
              <div className="relative">
                <input
                  type={showPwd ? 'text' : 'password'} required autoFocus value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full px-3 py-2.5 pr-10 bg-white border border-slate-300 rounded-lg text-sm
                             text-slate-900 placeholder-slate-400
                             focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  placeholder={t('team_passwordPlaceholder')}
                />
                <button type="button" tabIndex={-1} onClick={() => setShowPwd(s => !s)}
                        className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-slate-400 hover:text-slate-600">
                  {showPwd ? (
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none"
                         stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
                         className="w-4 h-4">
                      <path d="M17.94 17.94A10.07 10.07 0 0112 20c-7 0-11-8-11-8a18.45 18.45 0 015.06-5.94M9.9 4.24A9.12 9.12 0 0112 4c7 0 11 8 11 8a18.5 18.5 0 01-2.16 3.19m-6.72-1.07a3 3 0 11-4.24-4.24M1 1l22 22" />
                    </svg>
                  ) : (
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none"
                         stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
                         className="w-4 h-4">
                      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                      <circle cx="12" cy="12" r="3" />
                    </svg>
                  )}
                </button>
              </div>
            </div>
            {error && (
              <div className="flex items-start gap-2 p-3 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none"
                     stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
                     className="w-4 h-4 shrink-0 mt-0.5">
                  <circle cx="12" cy="12" r="10" /><path d="M12 8v4 M12 16h.01" />
                </svg>
                <span>{error}</span>
              </div>
            )}
          </form>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 bg-slate-50 border-t border-slate-200 flex justify-end gap-2">
          <button type="button" onClick={onCancel} disabled={submitting}
                  className="px-4 py-2 text-sm font-medium rounded-lg border border-slate-300 text-slate-700 bg-white hover:bg-slate-100 transition disabled:opacity-50">
            {t('team_cancel')}
          </button>
          <button type="submit" form="confirm-remove-form" disabled={submitting || !password}
                  className="px-4 py-2 text-sm font-medium rounded-lg bg-red-600 text-white hover:bg-red-700 transition shadow-sm disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-2">
            {submitting && (
              <svg className="w-4 h-4 animate-spin" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
              </svg>
            )}
            {submitting ? t('team_remove_removing') : (isSelf ? t('team_members_leaveOrg') : t('team_members_removeMember'))}
          </button>
        </div>
      </div>
    </div>
  );
}
